import { Divider, Stack, DialogContent, DialogActions, Dialog, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useForm, get } from 'react-hook-form';
import { useCallback, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';

import { Strings } from '@app/constants';
import { Header } from '@app/sections/header';
import { addClientSchema } from '../validators';
import { FormProvider, RHFTextField, RHFUploadAvatar, Scrollbar } from '@app/components';
import { IAddClientSchema, IClient } from '../types';
import { addClient, updateClient } from '../slice';
import { useRequestState } from '@app/hooks';
import { selectAddClientState, selectUpdateClientState } from '../selector';

interface Props {
  open: boolean;
  onClose: VoidFunction;
  row?: IClient;
}

const AddClient = ({ open, onClose, row }: Props) => {
  const dispatch = useDispatch();

  const methods = useForm<IAddClientSchema>({
    resolver: yupResolver(addClientSchema) as any,
    defaultValues: {
      firstName: get(row, 'firstName', ''),
      lastName: get(row, 'lastName', ''),
      phone: get(row, 'phone', ''),
      email: get(row, 'email', ''),
      companyName: get(row, 'companyName', ''),
      clientImage: row?.image ? row.image : '',
    },
  });
  const [isImageUploaded, setIsImageUploaded] = useState(false);

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });
      if (file) {
        methods.setValue('clientImage', newFile);
        setIsImageUploaded(true);
      }
    },
    [methods]
  );

  const onSubmit = (form: IAddClientSchema) => {
    row ? dispatch(updateClient({ ...form, id: row?.id })) : dispatch(addClient({ ...form }));
  };
  const { loading: saveClient } = useRequestState({
    stateSelector: row ? selectUpdateClientState : selectAddClientState,
    onSuccess: onClose,
    successMessageShown: true,
    errorShown: true,
  });
  return (
    <>
      <FormProvider methods={methods}>
        <Dialog open={open}>
          <Header
            variant="dialog"
            title={row ? Strings.clients.updateClient : Strings.clients.addClient}
            onClose={onClose}
          />
          <Divider />
          <Scrollbar>
            <DialogContent>
              <Stack my={3}>
                <RHFUploadAvatar
                  name={'clientImage'}
                  onDrop={handleDrop}
                  sx={{
                    border:
                      isImageUploaded || row?.clientImage
                        ? 'none'
                        : '5px dashed rgba(0, 0, 0, 0.08)',
                  }}
                />
              </Stack>
              <Stack my={3} justifyContent={'space-between'} direction={'row'} spacing={2}>
                <RHFTextField
                  required
                  name="firstName"
                  label={Strings.field.firstName}
                  fullWidth
                  variant="outlined"
                />
                <RHFTextField required label={Strings.field.lastName} name="lastName" fullWidth />
              </Stack>
              <Stack my={2} justifyContent={'space-between'} direction={'row'} spacing={2}>
                <RHFTextField label={Strings.field.phone} name="phone" fullWidth />
                <RHFTextField required label={Strings.field.email} name="email" fullWidth />
              </Stack>

              <Stack my={2} justifyContent={'space-between'} direction={'row'} spacing={2}>
                <RHFTextField
                  name="companyName"
                  label={Strings.field.companyName}
                  variant="outlined"
                />
              </Stack>
            </DialogContent>
          </Scrollbar>
          <Divider sx={{ pt: 2 }} />
          <DialogActions>
            <Button variant="outlined" color="inherit" onClick={onClose}>
              {Strings.button.cancel}
            </Button>
            <LoadingButton
              variant="contained"
              color="primary"
              type="submit"
              loading={saveClient}
              onClick={methods.handleSubmit(onSubmit)}
            >
              {Strings.button.save}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </FormProvider>
    </>
  );
};

export { AddClient };
